import { ChangeDetectorRef, Component, Input, OnInit, input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { takeUntil } from 'rxjs';
import { AnalyticsService } from '../../@core/utils';
import { CHANGE_TRIAL_ROLES } from '../../enum/lesson-plan-privacy';
import { ROLES } from '../../enum/roles.enum';
import { ClearObservableHelper } from '../../helpers/ClearObservableHelper';
import { SchoolCalendar, TeacherAcademicInformation } from '../../interface/calendar';
import { Grades } from '../../interface/grades';
import { Subjects } from '../../interface/subject';
import { User } from '../../interface/user';
import { AuthService } from '../../services/auth.service';
import { CalendarService } from '../../services/calendar.service';
import { GradeService } from '../../services/grade.service';
import { HttpService } from '../../services/http.service';
import { SchoolService } from '../../services/school.service';
import { StorageService } from '../../services/storage.service';
import { SubjectService } from '../../services/subject.service';
import { ToasterService } from '../../services/toaster.service';
import { MatDialogComponent } from '../mat-dialog/mat-dialog.component';

@Component({
  selector: 'ngx-teacher-academic-info',
  templateUrl: './teacher-academic-info.component.html',
  styleUrls: ['./teacher-academic-info.component.scss'],
})
export class TeacherAcademicInfoComponent extends ClearObservableHelper implements OnInit {
  teachingInfoForm: FormGroup;
  user: User | any;
  subjects: Subjects[];
  grades: Grades[] | any;
  @Input() title: string;
  @Input() changeRole;
  changeTrialRole;
  loading = false;
  @Input() isTeacherAdministration: boolean = false;
  @Input() userId: number;
  @Input() isSuperAdminActivity: boolean = true;
  // @ViewChild('callAPIDialog') callAPIDialog: TemplateRef<any>;
  isTeacherOnly = false;
  localStorageUserData: any;
  teacherAcademicId: any;
  currentCalendarId: any;
  selectedSchoolCalendar: SchoolCalendar; //variable to store the selected calendar for the selected school
  newAcademicYearTeachingInfo: TeacherAcademicInformation;
  constructor(
    private fb: FormBuilder,
    public storageService: StorageService,
    private authService: AuthService,
    public router: Router,
    private subjectService: SubjectService,
    private gradeService: GradeService,
    public matDialog: MatDialog,
    public alayticsService: AnalyticsService,
    private httpService: HttpService,
    private toasterService: ToasterService,
    private spinner: NgxSpinnerService,
    private route: ActivatedRoute,
    private calendarService: CalendarService,
    private cdr: ChangeDetectorRef,
    private schoolService: SchoolService,
  ) {
    super();
  }

  async ngOnInit() {
    try {
      this.getFormControls();
      const user = this.storageService.getItem('user');
      this.localStorageUserData = user.user;
      this.user = user.user;
      await this.spinner.show();
      this.selectedSchoolCalendar = this.storageService.getItem('selectedSchoolCalendarByAdmin');

      // Subscribe to changes in the selected school calendar from local storage to update the form data accordingly when the calendar is changed by the admin.
      this.storageService.setSelectedSchoolsCalendarToLocalStorage
        .pipe(takeUntil(this.destroy$))
        .subscribe(async (result) => {
          if (result) {
            this.spinner.show();
            try {
              this.selectedSchoolCalendar = result || this.storageService.getItem('selectedSchoolCalendarByAdmin');
              const academicInfoOfUser = await this.calendarService.academicInformationByCalendarAndUserId({
                school_calendar_id: !this.selectedSchoolCalendar?.isFirst ? this.selectedSchoolCalendar.id : 0,
                user_id: this.user?.id,
              });
              await this.patchProfileForm(academicInfoOfUser[0]);
            } catch (error) {
              console.error('Failed to update teacher academic info for selected calendar', error);
            } finally {
              await this.spinner.hide();
            }
          }
        });

      // this.loading = true;
      if (this.route.snapshot.paramMap.get('id')) {
        // Get the teacher academic info id from the route params
        this.teacherAcademicId = Number(this.route.snapshot.paramMap.get('id')) || 0;
        this.newAcademicYearTeachingInfo = await this.calendarService.academicInformationById(this.teacherAcademicId);
        this.patchProfileForm(this.newAcademicYearTeachingInfo);
      }
      this.currentCalendarId = await this.storageService.getCurrentSchoolCalendarYear();
      // Patch the user related data on the form
      await this.userData();
      await this.getSubjects();
      await this.getGrades();
      this.updateBellScheduleValidator();
      // this.loading = false;
      await this.spinner.hide();
    } catch (error) {
      this.loading = false;
      await this.spinner.hide();
    }
  }

  updateBellScheduleValidator() {
    // Ensure `this.user.role_id` is available before updating validators
    if (this.user && this.user.role_id !== undefined) {
      const bellSheduleControl = this.teachingInfoForm.get('bellShedule');
      if (this.user.role_id === ROLES.TEACHER_ONLY) {
        bellSheduleControl?.clearValidators(); // No validators for TEACHER_ONLY
      } else {
        bellSheduleControl?.setValidators(Validators.required); // Add required validator for others
      }
      bellSheduleControl?.updateValueAndValidity(); // Refresh validation state
    }
  }

  setSubjectCount(values) {
    this.teachingInfoForm.patchValue({
      teachingSubjectcount: values.length,
    });
  }

  getFormControls() {
    this.teachingInfoForm = this.fb.group({
      teachingSubjectcount: [''],
      teachingSubject: [[], Validators.required],
      teachingGrades: [[], Validators.required],
      bellShedule: [''],
      // designation: ['', Validators.required],
    });
  }

  async getSubjects() {
    try {
      this.subjects = (await this.subjectService.getAllSubjects()) as any[];
    } catch (error) {
      console.log(error);
    }
  }

  async getGrades() {
    try {
      this.grades = await this.gradeService.getAllGrades();
    } catch (error) {
      console.log(error);
    }
  }

  async userData() {
    try {
      // this.spinner.show();
      if (this.isSuperAdminActivity) {
        const user = this.storageService.getItem('user');
        this.user = user.user;
      } else {
        const reponse = await this.authService.userById(this.userId, false);
        this.user = reponse;
      }

      // Added for teacher only role to hide Will you utilize the School's Bell Schedule section.
      if (this.user.role_id === ROLES.TEACHER_ONLY) {
        this.isTeacherOnly = true;
      }

      if (!this.user?.is_trial_user) {
        if (this.title === 'profile') {
          // working on this is remaining
          if (this.selectedSchoolCalendar && this.selectedSchoolCalendar.id && !this.selectedSchoolCalendar.isFirst) {
            const academicInfoOfUser = await this.calendarService.academicInformationByCalendarAndUserId({
              school_calendar_id: !this.selectedSchoolCalendar?.isFirst ? this.selectedSchoolCalendar.id : 0,
              user_id: this.user?.id,
            });
            await this.patchProfileForm(academicInfoOfUser[0]);
          } else {
            await this.patchProfileForm(this.user);
          }
        } else {
          if (this.selectedSchoolCalendar && this.selectedSchoolCalendar.id && !this.selectedSchoolCalendar.isFirst) {
            const academicInfoOfUser = await this.calendarService.academicInformationByCalendarAndUserId({
              school_calendar_id: !this.selectedSchoolCalendar?.isFirst ? this.selectedSchoolCalendar.id : 0,
              user_id: this.user?.id,
            });
            this.newAcademicYearTeachingInfo = academicInfoOfUser[0];
            await this.patchProfileForm(academicInfoOfUser[0]);
          } else {
            await this.patchProfileForm(this.user);
          }
        }
        // await this.patchProfileForm(this.user);
      } else {
        await this.getDummyUserAndPatch('3');
      }

      if (this.title !== 'administration' && this.user?.is_trial_user) {
        await this.setTrialUserValues(this.user.teachingInfo.dummyUserId);
      }

      // this.spinner.hide();
    } catch (error) {
      console.log(error);
    }
  }

  async patchProfileForm(user: TeacherAcademicInformation | User) {
    try {
      // await this.getGrades();

      // if (user?.teachingInfo?.designation && !isTrialUser) {
      //   await this.setDesignation(user?.teachingInfo?.designation, true);
      // }
      await this.spinner.show();
      this.teachingInfoForm.patchValue({
        teachingSubjectcount: user?.teachingInfo.teachingSubjectcount,
        teachingSubject: user?.teachingInfo?.teachingSubject,
        teachingGrades: user?.teachingInfo?.teachingGrades,
        // examPerdays: user?.teachingInfo?.examPerdays,
        // Need to check default value for bellShedule as null, so that form is valid for teacher role
        bellShedule: user?.teachingInfo?.bellShedule ? user?.teachingInfo?.bellShedule : null,
        // designation: user?.teachingInfo?.designation,
      });
      await this.spinner.hide();
    } catch (error) {
      await this.spinner.hide();
      // console.log(error);
    }
  }

  async submit() {
    try {
      const user = this.storageService.getItem('user');
      if (user?.user?.is_trial_user) {
        if (this.router?.url?.includes('change-trial-user-role')) {
          await this.updateTrialUserAndRender(user.user);
          await this.router.navigateByUrl('/test-quizzes');
        } else {
          await this.updateTrialUserAndRender(user.user);
          this.router.navigateByUrl('/administration/teacher-periods');
        }
        return;
      } else {
        this.loading = true;
        if (this.router.url.includes('profile')) {
          // working on this is remaining
          if (this.selectedSchoolCalendar && this.selectedSchoolCalendar.id) {
            const body: any = {
              user_id: this.user.id,
              school_id: this.user.school_id,
              school_calendar_id: this.selectedSchoolCalendar ? this.selectedSchoolCalendar.id : 0,
              // teachingInfo: await this.teachingInformation(),
            };
            // if (this.teacherAcademicId) {
            const userExist = await this.calendarService.academicInformationByCalendarAndUserId({
              user_id: this.user.id,
              school_calendar_id: !this.selectedSchoolCalendar?.isFirst ? this.selectedSchoolCalendar.id : 0,
            });
            if (userExist && userExist?.[0]?.id && this.currentCalendarId === true) {
              // Update existing record for first calendar selection
              body['teachingInfo'] = {
                ...userExist?.[0]?.teachingInfo,
                teachingSubjectcount: this.teachingInfoForm.value.teachingSubject.length,
                teachingSubject: this.teachingInfoForm.value.teachingSubject,
                teachingGrades: this.teachingInfoForm.value.teachingGrades,
              };
              this.calendarService.updatePreviousYearTeacherAcademicInformation({
                user_id: userExist?.[0]?.id,
                teacher_academic_info: body.teachingInfo,
              });

              this.user.teachingInfo = body.teachingInfo;

              const updateUser = await this.authService.updateUser(this.user.id, this.user, true);

              if (updateUser) {
                const storageData = await this.storageService.getItem('user');
                this.storageService.removeItem('user');
                storageData.user = updateUser;
                this.storageService.setItem('user', storageData);
              }
            } else {
              // update existing record for other calendar selections
              body['teachingInfo'] = {
                ...userExist?.[0]?.teachingInfo,
                teachingSubjectcount: this.teachingInfoForm.value.teachingSubject.length,
                teachingSubject: this.teachingInfoForm.value.teachingSubject,
                teachingGrades: this.teachingInfoForm.value.teachingGrades,
              };
              body['isActive'] = userExist?.[0]?.isActive ? userExist?.[0]?.isActive : true;
              await this.calendarService.updateTeacherAcademicInformation(userExist?.[0]?.id, body);
            }
          }
        } else {
          const body = {
            user_id: this.user.id,
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            email: this.user.email,
            role_id: this.user.role_id,
            school_id: this.user.school_id,
            isNewUser: this.user?.isNewUser,
            profileData: {
              ...this.user.profileData,
              currentRoute:
                this.isTeacherOnly && this.title === 'profile' ? '/test-quizzes' : '/administration/teacher-periods',
            },
            password: this.user.password,
            teachingInfo: await this.teachingInformation(),
            user_profile_media_id: this.user?.user_profile_media_id,
            address: this.user.address,
            contact: this.user.contact,
            birthdate: this.user.birthdate,
            school_indentity_number: this.user?.school_indentity_number,
            is_trial_user: this.user.is_trial_user,
            uid: this.user.uid,
            provider_id: this.user.provider_id,
            proxy_email: this.user.proxy_email,
            last_login_location: this.user.last_login_location,
          };
          const updateUser: any = await this.authService.updateUser(this.user.id, body, true);
          if (updateUser) {
            this.storeLocalStorageUser(updateUser);
            const decodedUser: any = this.httpService.decodeLoginToken(updateUser);
            this.title === 'profile' || this.title === 'teacherNewAcademicYearSetup'
              ? await this.userData()
              : decodedUser?.teachingInfo?.bellShedule === 'true'
                ? this.router.navigateByUrl('/administration/teacher-periods')
                : decodedUser?.role_id === ROLES.TEACHER_ONLY
                  ? this.router.navigateByUrl('administration/calendar_setup')
                  : this.router.navigateByUrl(`/administration/school-instructional-peroids/teacher/${decodedUser.id}`);
            if (this.isTeacherOnly && this.title) {
              this.toasterService.showToast('Academic Information Added Successfully', 'success');
            }
          }
        }
      }

      this.loading = false;
    } catch (error) {
      this.loading = false;
      // console.log(error);
    }
  }

  private storeLocalStorageUser(user: User): void {
    const localStorageData = this.storageService.getItem('user');
    this.storageService.setItem('user', {
      authToken: localStorageData?.authToken,
      user,
    });
  }

  private async updateTrialUserAndRender(user) {
    try {
      const information = {
        dummyUserId: 3,
      };

      user.teachingInfo = information;

      const updateUser: any = await this.authService.updateUser(this.user.id, user, true);

      if (updateUser) {
        this.storeLocalStorageUser(updateUser);
      }
    } catch (error) {
      // console.log(error);
    }
  }

  async teachingInformation() {
    try {
      let bellScheduleData = [];
      const information = {
        teachingSubjectcount: this.teachingInfoForm.value.teachingSubject.length,
        teachingSubject: this.teachingInfoForm.value.teachingSubject,
        teachingGrades: this.teachingInfoForm.value.teachingGrades,
        // examPerdays: this.teachingInfoForm.value.examPerdays,
        bellShedule: this.teachingInfoForm.value.bellShedule,
        // designation: this.teachingInfoForm.value.designation,
        // schoolBellScheduleId: this.user?.teachingInfo?.schoolBellScheduleId,
        schoolBellScheduleId:
          this.title === 'teacherNewAcademicYearSetup'
            ? this.teachingInfoForm.value.bellShedule === 'true'
              ? this.user?.teachingInfo?.schoolBellScheduleId
              : false
            : this.user?.teachingInfo?.schoolBellScheduleId,
        teacherBellScheduleId:
          this.title === 'teacherNewAcademicYearSetup'
            ? this.teachingInfoForm.value.bellShedule === 'false'
              ? this.newAcademicYearTeachingInfo?.teachingInfo?.teacherBellScheduleId
              : null
            : (this.user?.teachingInfo?.teacherBellScheduleId ?? null),
      };
      // For new academic year setup, fetch the bell schedule based on the selected school calendar
      if (this.title === 'teacherNewAcademicYearSetup') {
        bellScheduleData = await this.schoolService.getSchoolBellSchedules({
          school_id: this.user.school_id,
          school_calendar_id: this.selectedSchoolCalendar?.id || 0,
          multiple_school_access: false,
        });
        information.schoolBellScheduleId =
          this.teachingInfoForm.value.bellShedule === 'true' ? bellScheduleData?.[0]?.id : false;
        information.teacherBellScheduleId =
          this.teachingInfoForm.value.bellShedule !== 'true'
            ? this.newAcademicYearTeachingInfo?.teachingInfo?.teacherBellScheduleId
            : null;
        return information;
      }
      return information;
    } catch (error) {
      console.log('teachingInformation', error);
    }
  }

  openConfirmationDialog(): void {
    try {
      if (this.teachingInfoForm.invalid) {
        Object.keys(this.teachingInfoForm.controls).forEach((field) => {
          const control = this.teachingInfoForm.get(field);

          control.markAsTouched({
            onlySelf: true,
          });
        });
        this.toasterService.showToast('Please fill in all mandatory fields', 'danger');
        this.loading = false;
        return;
      }

      if (this.title !== 'administration' && this.user?.is_trial_user) {
        this.toasterService.showToast('You can update teaching information from here', 'info');
        return;
      }

      if (this.title !== 'administration' && this.title !== 'teacherNewAcademicYearSetup') {
        this.submit();
      } else {
        this.openConfirmDialog();
      }
    } catch (error) {
      console.log(' openConfirmationDialog error', error);
    }
  }

  // async setDesignation(event, isShow = false) {
  //   try {
  //     if (this.user.is_trial_user && !isShow) {
  //       this.resetFormValues();
  //       await this.getGrades();
  //       if (event === 'Elementary') await this.setTrialUserValues(2);
  //       return;
  //     }

  //     if (!isShow) {
  //       this.teachingInfoForm.patchValue({
  //         teachingGrades: [],
  //       });
  //       await this.getGrades();
  //     }

  //     event === 'Elementary'
  //       ? this.grades.splice(7, 7)
  //       : this.grades.splice(0, 7);
  //   } catch (error) {
  //     // console.log(error);
  //   }
  // }

  private async setTrialUserValues(id: number) {
    try {
      const user: any = await this.authService.getDummyUserAsPerRole({
        dummy_users_id: CHANGE_TRIAL_ROLES[2].value,
        role_id: 9,
      });

      this.patchProfileForm(user[0]);
    } catch (error) {
      // console.log(error);
    }
  }

  // private resetFormValues(): void {
  //   try {
  //     Object.keys(this.teachingInfoForm.controls).forEach((ctrl) => {
  //       if (ctrl !== 'designation')
  //         this.teachingInfoForm.controls[ctrl].reset([]);
  //     });
  //   } catch (error) {
  //     // console.log(error);
  //   }
  // }

  public async getDummyUserAndPatch(event: string) {
    try {
      await this.getGrades();
      await this.setTrialUserValues(Number(event));
    } catch (error) {
      // console.log(error);
    }
  }

  public openConfirmDialog(): void {
    const dialogRef = this.matDialog.open(MatDialogComponent, {
      width: '400px',
      data: {
        title: 'Save Information',
        icon: '/assets/icons/save-featured-icon.svg',
        accessCodeLabel: 'Save Academic Information',
        confirmationMessage:
          'This will be a one time process to add academic information, You will not be able to modify this information after this screen',
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Save And Next',
        cancelTrackEvent: 'Cancel Save Information',
        confirmTrackEvent: 'Save Information',
        trackPage: 'Document',
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.title === 'teacherNewAcademicYearSetup' ? this.addTeacherAcademicsAndRender() : this.submit();
      }
    });
  }

  // For adding and updating the teacher academic information for new academic year setup
  async addTeacherAcademicsAndRender() {
    try {
      this.loading = true;

      if (this.title && this.title === 'teacherNewAcademicYearSetup') {
        const body: any = {
          user_id: this.user.id,
          school_id: this.user.school_id,
          school_calendar_id: this.currentCalendarId === true ? '' : this.currentCalendarId,
          teachingInfo: await this.teachingInformation(),
        };
        let response;
        if (this.teacherAcademicId) {
          response = await this.calendarService.updateTeacherAcademicInformation(this.teacherAcademicId, body);
        } else {
          const checkIfUserExist = await this.calendarService.academicInformationByCalendarId({
            user_id: this.user.id,
            school_id: this.user.school_id,
            school_calendar_id: this.currentCalendarId,
          });
          if (checkIfUserExist && checkIfUserExist?.[0]?.id) {
            response = await this.calendarService.updateTeacherAcademicInformation(checkIfUserExist?.[0].id, body);
          } else {
            response = await this.calendarService.addTeacherAcademicInformation(body);
          }
        }
        if (response && response.id) {
          this.teachingInfoForm.value.bellShedule === 'true'
            ? await this.router.navigateByUrl('/teacher-management/teacher-weekly-period')
            : this.router.navigateByUrl('/teacher-management/teacher-instructional-period');
          this.cdr.detectChanges();
        }
      }
      this.loading = false;
    } catch (error) {
      console.log('addTeacherAcademicsAndRender', error);
    }
  }

  //Handles changes to the selected school calendar by subscribing to updates from local storage.
  async handleCalendarChangeData() {
    this.storageService.setSelectedSchoolsCalendarToLocalStorage
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (res) => {
        if (res) {
          this.spinner.show();
          this.selectedSchoolCalendar = this.storageService.getItem('selectedSchoolCalendarByAdmin');
          await this.userData();
          this.spinner.hide();
        }
      });
  }
}
